<template>
  <CRow>
    <CCol col="12" xl="12">
      <transition name="slide">
        <CCard>
          <CCardBody>
            <div class="row justify-content-end align-items-center">
              <form class="form-inline">
                <div class="form-group">
                  <CSelect
                    :value.sync="payment_type"
                    :plain="true"
                    :options="payments"
                    :placeholder="$t('fields.payment_type_placeholder')"
                    :label="`${$t('fields.payment_type')}:`"
                    addInputClasses="mx-sm-3"
                    @update:value="paymentChanged"
                  />
                </div>
                <div class="form-group">
                  <label for="from-date">{{ $t('from') }}:</label>
                  <input type="date" id="from-date" v-model="from" class="form-control mx-sm-3">
                </div>
                <div class="form-group">
                  <label for="to-date">{{ $t('to') }}:</label>
                  <input type="date" id="to-date" v-model="to" class="form-control mx-sm-3">
                </div>
                <CButton color="primary" @click="filterClicked">
                  <CIcon :content="$options.filterIcon" /> {{ $t('filter') }}
                </CButton>
              </form>
            </div>

            <div v-if="loadError" class="alert alert-danger" style="white-space: pre-wrap;">{{ loadError }}</div>

            <CDataTable
              :items="items"
              :fields="fields"
              :items-per-page="perPage"
              :itemsPerPageSelect="itemsPerPageSelectConfig"
              :tableFilter="tableFilter"
              :table-filter-value.sync="search"
              :loading="loading"
              addTableClasses="d-print-table table-dropdown-fix"
              hover
              sorter
              @pagination-change="onPerPageChange"
            >
              <!-- Templates de columnas -->
              <template #payment_type="{item}">
                <td>
                  <CBadge :color="item.payment_class">{{item.payment_type}}</CBadge>
                </td>
              </template>
              <template #pay="{item}">
                <td>
                  <CBadge :color="item.payment_class">{{item.pay}}</CBadge>
                </td>
              </template>
              <template #amount="{item}">
                <td>${{item.amount}}</td>
              </template>
              <template #attended_by="{item}">
                <td>{{item.user}}</td>
              </template>
              <template #options="{item}">
                <td>
                  <div class="dropdown">
                    <button class="btn btn-secondary dropdown-toggle" @click="openOptions" type="button" data-toggle="dropdown">
                      {{ $t('button.options') }}
                    </button>
                    <div class="dropdown-menu">
                      <button class="dropdown-item" type="button" @click="showOrder(item.id)">{{ $t('button.details') }}</button>
                      <button class="dropdown-item" type="button" @click="editOrder(item.id)">{{ $t('button.edit') }}</button>
                      <button class="dropdown-item" type="button" @click="deleteOrder(item.id)">{{ $t('button.delete') }}</button>
                    </div>
                  </div>
                </td>
              </template>
            </CDataTable>

            <div class="d-flex justify-content-between align-items-center mt-3" v-if="totalRecords > 0">
              <div class="text-muted">Total: {{ totalRecords }}</div>
              <CPagination
                v-if="totalPages > 1"
                :activePage.sync="currentPage"
                :pages="totalPages"
                @update:activePage="onPageChange"
              />
            </div>
          </CCardBody>
        </CCard>
      </transition>
    </CCol>
  </CRow>
</template>

<script>
import axios from 'axios';
import listMixin from '../../mixins/list';
import localeMixin from '../../mixins/locale';
import i18n from "../../i18n";
import { cilFilter } from '@coreui/icons';

export default {
  name: 'OrderHistory',
  mixins: [listMixin, localeMixin],
  filterIcon: cilFilter,
  data() {
    return {
      items: [],
      fields: [
        { key: 'driver', label: i18n.t('fields.driver') },
        { key: 'name', label: i18n.t('fields.name') }, 
        { key: 'address', label: i18n.t('fields.address')},
        { key: 'address2', label: i18n.t('fields.address2')},
        { key: 'city', label: i18n.t('fields.city')},
        { key: 'phone', label: i18n.t('fields.phone')},
        { key: 'phone2', label: i18n.t('fields.phone2')},
        { key: 'quantity', label: i18n.t('fields.quantity') },
        { key: 'payment_type', label: i18n.t('fields.payment_type') }, 
        { key: 'pay', label: i18n.t('pay') }, 
        { key: 'amount', label: i18n.t('fields.amount') }, 
        { key: 'order_date', label: i18n.t('fields.order_date') }, 
        { key: 'attended_by', label: i18n.t('fields.attended_by') }, 
        {
          key: 'options',
          label: i18n.t('button.options'),
          sorter: false,
          filter: false
        }
      ],
      you: null,
      from: '',
      to: '',
      payment_type: 0,
      payments: [],
      tableFilter: {
        label: i18n.t('search'),
        placeholder: i18n.t('clients.search_placeholder'),
        lazy: false,
        external: true
      },
      search: '',
      perPage: 10,
      itemsPerPageSelectConfig: {
        external: true,
        values: [10, 25, 50, 100]
      },
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      searchDebounceTimer: null,
      loading: false,
      loadError: ''
    }
  },
  watch: {
    search() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.getOrders(1);
      }, 450);
    }
  },
  mounted() {
    this.getOrders(1);
    this.getPaymentTypes();
  },
  methods: {
    showOrder(id) {
      this.$router.push({ name: 'Order', params: {id} });
    },
    editOrder(id) {
      this.$router.push({ name: 'EditOrder', params: {id} });
    },
    async deleteOrder(id) {
      try {
        const result = await this.$swal({
          title: i18n.t('orders.modal.delete_label'),
          html: i18n.t('orders.modal.delete_body'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: i18n.t('button.accept')
        });
        
        if (result.isConfirmed) {
          await axios.delete(`/api/orders/${id}?token=${localStorage.getItem("api_token")}`);
          this.$swal(
            i18n.t('orders.modal.deleted_label'),
            i18n.t('orders.modal.deleted_body'),
            'success'
          );
          this.getOrders(this.currentPage);
        }
      } catch (error) {
        console.error("Error deleting order:", error);
        this.$swal(
          i18n.t('error'),
          i18n.t('orders.modal.delete_error'),
          'error'
        );
      }
    },
    async getOrders(page) {
      const targetPage = Number.isInteger(page) && page > 0 ? page : 1;
      this.loading = true;
      this.loadError = '';
      try {
        const token = localStorage.getItem("api_token");
        const params = new URLSearchParams();
        params.append('token', token || '');
        params.append('page', targetPage);
        params.append('per_page', this.perPage);

        if (this.from) params.append('from', this.from);
        if (this.to) params.append('to', this.to);
        if (this.payment_type !== 0) params.append('payment', this.payment_type);
        if (this.search) params.append('search', this.search);

        const url = `/api/orders/history?${params.toString()}`;
        const fullUrl = (axios.defaults.baseURL || '') + url;
        const response = await axios.get(url);
        const payload = response.data || {};

        this.items = Array.isArray(payload.data) ? payload.data : [];
        this.totalRecords = Number(payload.total || 0);
        this.totalPages = Number(payload.last_page || 1);
        this.currentPage = Number(payload.current_page || targetPage);

        if (this.items.length === 0) {
          this.loadError = `Diagnostico: peticion OK (HTTP ${response.status}) a ${fullUrl} pero el servidor devolvio 0 registros. total=${this.totalRecords}`;
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        this.items = [];
        this.totalRecords = 0;
        this.totalPages = 1;

        const status = error.response?.status;
        const serverMsg = error.response?.data ? JSON.stringify(error.response.data).slice(0, 300) : '';
        this.loadError = `Diagnostico: fallo la peticion. status=${status || 'sin respuesta (network error / bloqueado)'} mensaje="${error.message}" respuesta_servidor="${serverMsg}"`;

        if (status === 401) {
          this.$router.push({ name: 'Login' });
        }
      } finally {
        this.loading = false;
      }
    },
    filterClicked() {
      this.getOrders(1);
    },
    onPageChange(page) {
      this.getOrders(page);
    },
    onPerPageChange(newPerPage) {
      this.perPage = Number(newPerPage) || 10;
      this.getOrders(1);
    },
    async getPaymentTypes() {
      try {
        const response = await axios.get(`/api/payment-types?token=${localStorage.getItem('api_token')}`);
        this.payments = [
          { label: "All", value: 0 },
          ...response.data.map(item => ({
            label: item.name,
            value: item.id
          }))
        ];
      } catch (error) {
        console.error("Error fetching payment types:", error);
        if (error.response?.status === 401) {
          this.$router.push({ name: 'Login' });
        }
      }
    },
    paymentChanged(value) {
      this.payment_type = value;
      this.getOrders(1);
    },
    openOptions(e) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
}
</script>